import { ReactNode } from 'react'

import { useSiteGetApi } from '@/common/api/modules/site'
import { analysisTypeLabel } from '@/common/app/modules/media/mediaAnalysis'
import { mediaAnalysisHistoryPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { dateTimeStr } from '@/common/form/date'
import {
  MediaAnalysisProgress,
  MediaAnalysisStatus,
  MediaAnalysisType,
  isLLMOAnalysisType
} from '@/common/model/media/mediaAnalysis'
import { ChildProps } from '@/common/util/props'
import { HttpStatusCode } from '@/common/util/status'

import BackButton from '@/components/view/atoms/button/BackButton'
import AiLoading from '@/components/view/atoms/loading/AiLoading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'

/** 分析結果詳細APIのレスポンスのうち、この共通ページが参照する項目 */
export interface MediaAnalysisResultDetail {
  status?: MediaAnalysisStatus
  progress?: MediaAnalysisProgress
  created_at: string | null
}

interface Props extends ChildProps {
  /** 分析種別。タイトル・分析中の見出し・バッジはここから決まる */
  type: MediaAnalysisType
  /** 分析結果詳細APIのレスポンス（未取得は undefined） */
  data: MediaAnalysisResultDetail | undefined
  /** 分析結果詳細APIのエラー */
  error: Response | { status: HttpStatusCode } | undefined
  /** タイトル説明に出す対象名。省略時はサイトのドメインを使う */
  targetDomain?: string
  /** AI応答のJSON解析に失敗した場合のメッセージ（result_data.error） */
  resultError?: string
  /** タイトル右のボタン（再分析など） */
  rightNode?: ReactNode
}

/**
 * サイト現状分析（9種別）の分析結果画面の共通ベース。
 *
 * 種別によらず共通の「取得中・取得エラー／戻る／タイトル／分析中のAiLoading／
 * AI応答の解析失敗」までを担い、分析結果そのものの描画だけを children に委ねる。
 * 各分析はバックグラウンド実行のため、詳細画面では status=analyzing の間
 * 進捗（progress）を表示して完了を待つ。
 */
const MediaAnalysisResultPageBase = ({
  type,
  data,
  error,
  targetDomain,
  resultError,
  rightNode,
  children
}: Props) => {
  const siteId = useSiteId()
  const { data: site } = useSiteGetApi({ site_id: siteId })

  const analysisName = analysisTypeLabel(type)
  const analyzing = data?.status === 'analyzing'
  const progress = data?.progress
  const subject = targetDomain || site?.domain || ''
  const executedAt = `実行日時: ${data?.created_at ? dateTimeStr(data.created_at) : '-'}`

  return (
    <DetailPageLoader error={error} data={data}>
      <Main type={MainLayoutType.MEDIUM}>
        <BackButton link={mediaAnalysisHistoryPath(siteId)} />
        <PageTitle
          title={`${analysisName}結果`}
          description={subject ? `${subject} ／ ${executedAt}` : executedAt}
          badgeNode={
            <AppTitleBadge
              type={
                isLLMOAnalysisType(type) ? AppTitleBadgeType.MEDIA_GEO : AppTitleBadgeType.MEDIA
              }
            />
          }
          rightNode={rightNode}
        />
        {analyzing ? (
          <div className='flex size-full flex-col items-center justify-center py-32'>
            <AiLoading
              description={progress?.message || `${analysisName}を実施中です`}
              prompt={
                progress?.percent != null
                  ? `${progress.percent}% : ${progress.detail || ''}`
                  : undefined
              }
            />
          </div>
        ) : resultError ? (
          <Area bottomMargin={16} bgColor='caution'>
            <AreaContent>
              <Text size={14} weight='bold' color={TextColor.CRITICAL}>
                {resultError}
              </Text>
              <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
                AIの応答を解析できなかったため、分析結果を表示できません。お手数ですが分析を再実行してください。
              </Text>
            </AreaContent>
          </Area>
        ) : (
          children
        )}
      </Main>
    </DetailPageLoader>
  )
}

export default MediaAnalysisResultPageBase
